import {
  forwardRef,
  useLayoutEffect,
  useState,
  type ImgHTMLAttributes,
} from "react";
import { useQuery, type QueryFunctionContext } from "@tanstack/react-query";
import { api } from "../../api";
import { useAuth } from "../../auth";

export type ProtectedPhotoVariant = "full" | "thumbnail";

type ProtectedPhotoObjectSource = {
  cacheKey: string;
  blob: Blob;
  url: string;
};

export function currentProtectedPhotoObjectUrl(
  source: ProtectedPhotoObjectSource | undefined,
  cacheKey: string,
  blob: Blob | undefined,
) {
  return source?.cacheKey === cacheKey && source.blob === blob
    ? source.url
    : undefined;
}

export function protectedPhotoQueryKey(
  userId: string | undefined,
  photoId: string,
  variant: ProtectedPhotoVariant,
) {
  return ["protected-photo", userId ?? "anonymous", photoId, variant] as const;
}

export function protectedPhotoQueryOptions(
  userId: string | undefined,
  photoId: string,
  variant: ProtectedPhotoVariant,
) {
  const queryKey = protectedPhotoQueryKey(userId, photoId, variant);
  return {
    queryKey,
    queryFn: ({ signal }: QueryFunctionContext<typeof queryKey>) =>
      api.getPhotoBlob(photoId, variant, signal),
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  } as const;
}

export const ProtectedImage = forwardRef<
  HTMLImageElement,
  Omit<ImgHTMLAttributes<HTMLImageElement>, "src"> & {
    photoId: string;
    variant?: ProtectedPhotoVariant;
  }
>(function ProtectedImage(
  {
    photoId,
    variant = "thumbnail",
    className = "",
    alt,
    loading = "lazy",
    decoding = "async",
    ...props
  },
  ref,
) {
  const { user } = useAuth();
  const query = useQuery({
    ...protectedPhotoQueryOptions(user?.id, photoId, variant),
    enabled: Boolean(user?.id && photoId),
  });
  const cacheKey = `${user?.id ?? "anonymous"}:${photoId}:${variant}`;
  const [objectSource, setObjectSource] = useState<ProtectedPhotoObjectSource>();

  useLayoutEffect(() => {
    if (!query.data) {
      setObjectSource(undefined);
      return;
    }
    const nextUrl = URL.createObjectURL(query.data);
    setObjectSource({
      cacheKey,
      blob: query.data,
      url: nextUrl,
    });
    return () => URL.revokeObjectURL(nextUrl);
  }, [cacheKey, query.data]);

  // Gate the URL synchronously so a blob belonging to the previous
  // member/photo/variant can never render after the query key changes.
  const objectUrl = currentProtectedPhotoObjectUrl(
    objectSource,
    cacheKey,
    query.data,
  );

  return (
    <img
      {...props}
      ref={ref}
      src={objectUrl}
      alt={alt ?? ""}
      loading={loading}
      decoding={decoding}
      className={`${className} protected-image ${objectUrl ? "is-ready" : "is-loading"}`.trim()}
    />
  );
});
